/* eslint-disable react-refresh/only-export-components -- intentional: getAccessToken and useAuth are module-level exports co-located with AuthProvider by design */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase.ts';
import { setErrorUser } from '../lib/errorTracking';

interface AuthValue {
  session: Session | null;
  accessToken: string | null;
  user: User | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

// Module-level mirror so non-React code (apiClient, the WS hook) can read the
// current token without prop-drilling. Kept in sync by the provider.
let currentToken: string | null = null;
export function getAccessToken(): string | null {
  return currentToken;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      currentToken = data.session?.access_token ?? null;
      // The account UUID only — never the email. See lib/errorTracking.
      setErrorUser(data.session?.user?.id ?? null);
      setSession(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      currentToken = next?.access_token ?? null;
      setErrorUser(next?.user?.id ?? null);
      setSession(next);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const value: AuthValue = {
    session,
    accessToken: session?.access_token ?? null,
    user: session?.user ?? null,
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Like useAuth but returns null outside an AuthProvider (tests, isolated renders). */
export function useMaybeAuth(): AuthValue | null {
  return useContext(AuthContext);
}
